import { View } from '@adobe/react-spectrum';
import { useBreakpoint } from '@react-spectrum/utils';
import { error } from '@react/react-spectrum/Toast';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { browseApi, handleApiCall } from '@exchange-frontends/api';
import { Carousel, ErrorBoundary, SectionHeading } from '@exchange-frontends/components';
import { CarouselItem } from '@exchange-frontends/components/src/Carousel';
import { useMediaQuery } from '@exchange-frontends/custom-hooks';

import { ContainerContext } from '../../../../utils';
import { useStore } from '../../store';
import FeaturedAppCard from '../app-cards/FeaturedAppCard';

const FeaturedSectionHeading = styled(SectionHeading)`
  text-transform: initial;
  font-size: 14px;
  font-weight: normal;
  margin-top: 0;
`;

const FeaturedSection = () => {
  const {
    matchedBreakpoints: [breakpoint],
  } = useBreakpoint();
  const selectedProduct = useStore((state) => state.selectedProduct);
  const [visibleFeaturedApps, setVisibleFeaturedApps] = useState(4);
  const [apps, setApps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [displayedApps, setDisplayedApps] = useState([null, null, null, null]);
  const { ims } = useContext(ContainerContext);
  const accessToken = useMemo(() => ims?.getAccessToken()?.token, [ims]);
  const cloud = useStore((state) => state.cloud);

  const isSmallSize = useMediaQuery('(max-width: 450px)');

  useEffect(() => {
    switch (breakpoint) {
      case 'XXL':
      case 'XL':
        setVisibleFeaturedApps(4);
        break;
      case 'S':
        setVisibleFeaturedApps(2);
        break;
      case 'L':
      case 'M':
        setVisibleFeaturedApps(3);
        break;
      default:
        if (isSmallSize) {
          setVisibleFeaturedApps(1);
        } else {
          setVisibleFeaturedApps(2);
        }
        break;
    }
  }, [breakpoint, isSmallSize]);

  useEffect(() => {
    let mounted = true;
    const handler = handleApiCall(browseApi.featured, { cloud, authToken: accessToken });
    (async () => {
      try {
        const response = await handler.run();
        if (mounted) {
          setApps(response);
        }
      } catch (err) {
        // 20 is the error code for aborted requests
        if (err.code !== 20) {
          error(err.userMessage, { timeout: 0 });
          console.error(err);
          setErrorMessage(err.userMessage);
        }
      }
    })();
    return () => {
      mounted = false;
      handler.controller.abort();
    };
  }, [cloud, accessToken]);

  useEffect(() => {
    if (apps && selectedProduct) {
      setLoading(false);
      setDisplayedApps(apps.getProductApps(selectedProduct));
    }
  }, [selectedProduct?.key, apps?.original.length]);

  return (displayedApps?.length ?? 0) > 0 ? (
    <View>
      <FeaturedSectionHeading>Featured</FeaturedSectionHeading>
      <ErrorBoundary isError={errorMessage?.length > 0} errorMessage="Failed to load featured apps.">
        {() => (
          <Carousel
            fullWidthElements={visibleFeaturedApps === 1}
            visibleElements={visibleFeaturedApps}
            hideDots
            gap={16}
            data-testid="featured-carousel"
          >
            {displayedApps?.map((item, index) => (
              <CarouselItem
                key={item ? `${item.id}.${item.name}.${item.type}` : index}
                data-partner-level={item?.publisher?.level}
                data-partner-host-apps={item?.hostApps?.toString()}
              >
                <FeaturedAppCard
                  loading={loading}
                  title={item?.name}
                  subtitle={item?.publisher?.name}
                  image={item?.assets?.find((a) => a.type === 'featured')?.url ?? item?.featuredImageUrl}
                  appId={item?.id}
                  appType={item?.type}
                />
              </CarouselItem>
            ))}
          </Carousel>
        )}
      </ErrorBoundary>
    </View>
  ) : null;
};

export default FeaturedSection;
